import { CombinedStepType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { DEFAULT_DOCUMENT_ALLOWED_FILE_TYPES } from 'dashboard/lib/stubs/verification-template-version';
import {
  AutoclassificationResponseType,
  IdClassesByCountry,
} from 'inquiry/redux/thunk-actions/verification';
import { ApiModel } from 'lib/interfaces/api';
import {
  AutoClassificationConfig,
  ClientStepKeys,
  CombinedStepClientConfig,
  CombinedStepClientData,
  DeviceHandoffAssets,
  DeviceHandoffLocalizations,
  Disclaimer,
  PlatformLocalizations,
  StepCombinedBaseConfig,
  StepCompleteConfig,
  StepDocumentConfig,
  StepGovernmentIdConfig,
  StepIntegrationConfigMxBank,
  StepSelfieConfig,
  StepSelfieReflectionConfig,
  StepUiConfig,
  StepVerifyWithPersonaConfig,
  UiStepType,
} from 'lib/interfaces/StepsConfig';
import { UiComponentType } from 'lib/interfaces/ui-components';
import {
  AutoCaptureRuleType,
  ClientSideOverlay,
  GovernmentIdSide,
  GovIdCapturePage,
  RequiredDevice,
  SelfieMatch,
} from 'lib/interfaces/verificationConfig';
import { assertNever } from 'lib/utils/types';
import { SelfieStep } from 'verify/lib/interfaces/selfie';
import { createMockInquiry } from 'verify/lib/stubs/inquiry';
import { InquiryAttributes, InquiryRelationships, PollingMode } from 'verify/models/inquiry';

export const MOCK_WAIT_FOR_TRANSITION_ATTRIBUTE: Record<PollingMode, Partial<InquiryAttributes>> = {
  [PollingMode.BACKGROUND]: {
    'wait-for-transition': {
      'interval-ms': 2000,
      'max-attempts': 90,
      'polling-mode': PollingMode.BACKGROUND,
    },
  },
  [PollingMode.BLOCKING]: {
    'wait-for-transition': {
      'interval-ms': 1000,
      'max-attempts': 90,
      'polling-mode': PollingMode.BLOCKING,
    },
  },
  [PollingMode.NONE]: {
    'wait-for-transition': {
      'polling-mode': PollingMode.NONE,
    },
  },
};

export function createMockDynamicFlowInquiry(
  attributes?: Partial<InquiryAttributes>,
  relationships?: Partial<InquiryRelationships>,
  id?: string,
) {
  return createMockInquiry(
    {
      logo: {
        'content-type':
          attributes?.['next-step']?.styles?.logo?.base?.base?.['content-type'] || null,
        display: attributes?.['next-step']?.styles?.logo?.base?.base?.display || null,
        url: attributes?.['next-step']?.styles?.logo?.base?.base?.['url'] || null,
      },
      ...attributes,
    },
    {
      template: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        data: null,
      },
      'inquiry-template-version': {
        data: {
          id: 'app_id_itmplv',
          type: ApiModel.InquiryTemplateVersion,
        },
      },
      ...relationships,
    },
    id,
  );
}

/**
 * Creates a dynamic flow Inquiry where the next step is a preset document step.
 */
export function createMockDocumentInquiry(
  config: Partial<StepDocumentConfig> = {},
  pollingMode: PollingMode = PollingMode.NONE,
) {
  return createMockDynamicFlowInquiry({
    'next-step': createMockCombinedStep(UiStepType.Document, config),
    ...MOCK_WAIT_FOR_TRANSITION_ATTRIBUTE[pollingMode],
  });
}

/**
 * Creates a dynamic flow Inquiry where the next step is a preset government ID step.
 */
export function createMockGovernmentIdInquiry(
  config: Partial<StepGovernmentIdConfig> = {},
  pollingMode: PollingMode = PollingMode.NONE,
  attributes: Partial<InquiryAttributes> = {},
) {
  return createMockDynamicFlowInquiry({
    'next-step': createMockCombinedStep(UiStepType.GovernmentId, config),
    ...MOCK_WAIT_FOR_TRANSITION_ATTRIBUTE[pollingMode],
    ...attributes,
  });
}

export function createMockSelfieInquiry(config: Partial<StepSelfieConfig> = {}) {
  return createMockDynamicFlowInquiry({
    'next-step': createMockCombinedStep(UiStepType.Selfie, config),
  });
}

/**
 * Creates mock combined step
 */
export function createMockCombinedStep(
  type: CombinedStepType,
  config: Partial<CombinedStepClientConfig> = {},
  name?: string,
): CombinedStepClientData {
  switch (type) {
    case UiStepType.Document:
      return {
        type: type,
        name: name || ClientStepKeys.Document,
        config: createMockDocumentConfig(config as StepDocumentConfig),
      };
    case UiStepType.GovernmentId:
      return {
        type: type,
        name: name || ClientStepKeys.GovernmentId,
        config: createMockGovernmentIdConfig(config as StepGovernmentIdConfig),
      };
    case UiStepType.Selfie:
      return {
        type: type,
        name: name || ClientStepKeys.Selfie,
        config: createMockSelfieConfig(config as StepSelfieConfig),
      };
    case UiStepType.SelfieReflection:
      return {
        type: type,
        name: name || ClientStepKeys.SelfieReflection,
        config: createMockSelfieReflectionConfig(config as StepSelfieReflectionConfig),
      };
    default:
      assertNever(type);
  }
}

export function createMockDocumentConfig(
  config: Partial<StepDocumentConfig> = {},
): StepDocumentConfig {
  return {
    ...createMockBaseCombinedConfig(),
    'enabled-capture-options-desktop': ['web_camera', 'upload', 'device_handoff'],
    'enabled-capture-options-mobile': ['web_camera', 'mobile_camera', 'upload', 'device_handoff'],
    'field-key-document': 'document',
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    'document-id': undefined,
    'start-page': 'prompt',
    'document-file-limit': 30,
    'pending-page-text-vertical-position': null,
    'accepted-file-types': DEFAULT_DOCUMENT_ALLOWED_FILE_TYPES,
    kind: 'my_cool_doc',
    localizations: createMockDocumentLocalizations(),
    assets: createMockDocumentAssets(),
    ...config,
  };
}

export function createMockGovernmentIdConfig(
  config: Partial<StepGovernmentIdConfig> = {},
): StepGovernmentIdConfig {
  return {
    ...createMockBaseCombinedConfig(),
    disclaimer: Disclaimer.None,
    'audio-enabled': true,
    'barcode-camera-manual-capture-delay-ms': 8000,
    'pending-page-text-vertical-position': null,
    'camera-error-device-handoff-options': [],
    'govid-design-version': 1,
    'enabled-skip-unknown': false,
    'enabled-capture-options-desktop': ['web_camera', 'upload', 'device_handoff'],
    'enabled-capture-options-mobile': ['web_camera', 'mobile_camera', 'upload', 'device_handoff'],
    'enabled-capture-file-types': ['image'],
    'enabled-file-types': [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/heic',
      'image/heif',
      'image/tif',
      'image/tiff',
      'application/pdf',
    ],
    'always-show-mobile-camera-capture-option': false,
    idclasses: [
      {
        class: 'dl',
        'requires-sides': [GovernmentIdSide.Front, GovernmentIdSide.Back],
        'requires-device': RequiredDevice.None,
      },
      {
        class: 'cid',
        'requires-sides': [GovernmentIdSide.Front, GovernmentIdSide.Back],
        'requires-device': RequiredDevice.None,
      },
    ],
    'field-key-country': 'country',
    'field-key-document': 'document',
    'field-key-idclass': 'idclass',
    localizations: createMockGovIdLocalizations(),
    'localization-overrides': [],
    assets: createMockGovernmentIdAssets(),
    'selected-country-code': 'US',
    'video-session-jwt': null,
    'video-capture-methods': ['stream'],
    'auto-classification-config': createMockAutoclassificationConfig({ 'is-enabled': false }),
    ...config,
  };
}

export function createMockSelfieConfig(config: Partial<StepSelfieConfig> = {}): StepSelfieConfig {
  return {
    ...createMockBaseCombinedConfig(),
    'selfie-type': SelfieMatch.ProfileAndCenter,
    'audio-enabled': true,
    'web-camera-allow-stillfie-fallback': true,
    'field-key-selfie': 'current_selfie',
    'enable-mobile-pending-experience-for-web': false,
    'enabled-capture-options-desktop': ['web_camera', 'device_handoff'],
    'enabled-capture-options-mobile': ['web_camera', 'device_handoff'],
    'enabled-capture-file-types': ['image'],
    'ordered-poses': [SelfieStep.Center, SelfieStep.Left, SelfieStep.Right],
    'skip-prompt-page': false,
    'video-session-jwt': null,
    'video-capture-methods': ['stream'],
    localizations: createMockSelfieLocalizations(),
    assets: createMockSelfieAssets(),
    ...config,
  };
}

export function createMockSelfieReflectionConfig(
  config: Partial<StepSelfieReflectionConfig> = {},
): StepSelfieReflectionConfig {
  return {
    ...createMockBaseCombinedConfig(),
    'selfie-type': SelfieMatch.Reflection,
    'field-key-selfie': 'current_selfie',
    'enabled-capture-options-desktop': ['web_camera', 'device_handoff'],
    'enabled-capture-options-mobile': ['web_camera', 'device_handoff'],
    localizations: {
      ...createMockPlatformLocalizations(),
      ...createMockDeviceHandoffLocalizations(),
    },
    assets: { ...createMockDeviceHandoffAssets() },
    ...config,
  };
}

function createMockBaseCombinedConfig(): StepCombinedBaseConfig {
  return {
    'back-step-enabled': false,
    'cancel-button-enabled': true,
    'device-handoff-options': [],
    'web-camera-manual-capture-delay-ms': 0,
    'pending-page-text-vertical-position': null,
    'device-handoff-email-prefill': '',
    'device-handoff-email-disabled': false,
    'device-handoff-phone-number-prefill': '',
    'device-handoff-phone-number-disabled': false,
    localizations: createMockPlatformLocalizations(),
  };
}

export function createMockPlatformLocalizations(
  localizations: Partial<PlatformLocalizations> = {},
): PlatformLocalizations {
  return {
    'cancel-dialog': {
      'btn-resume': 'Resume',
      'btn-submit': 'Cancel',
      prompt: 'Are you sure you want to cancel?',
      title: 'Cancel Verification',
    },
    'new-device-fraud-warning-dialog': {
      'btn-continue': 'Yes this was me',
      'btn-cancel': 'No I have been h4x0r3d',
      prompt: 'Is this really you?',
      title: 'This is a new device',
    },
    'unsupported-browser-warning-dialog': {
      'btn-continue': 'Ok',
      prompt: 'You‘re too old, let go, it‘s over, nobody uses Internet Explorer',
      title: 'Get a new browser',
    },
    'phishing-signal-dialog': {
      'btn-continue': 'Yes this was me',
      'btn-learn-more': 'Learn more',
      prompt: 'Is this really you?',
      title: 'This is a new device',
    },
    ...localizations,
  };
}

export function createMockDeviceHandoffLocalizations(): DeviceHandoffLocalizations {
  return {
    'device-handoff-prompt-page': {
      description:
        'To verify your identity, we need access to a camera. We’ll send you a secure link for continuing on your phone. No app download is required.',
      title: 'Continue on another device',
      'qr-code': '',
      'via-email': 'Send Email',
      'via-sms': 'Send SMS',
    },
    'device-handoff-started-page': {
      'btn-resume': 'Continue on this device',
      prompt: 'You’ve started working on another device. Click the button below to continue here.',
      title: 'Continue on other device',
    },
    'device-handoff-email-prompt-page': {
      'btn-submit': 'Send secure link',
      prompt:
        'To verify your identity, we need access to a camera. We’ll email you a secure link for taking photos. No app download is required.',
      title: 'Continue on another device',
    },
    'device-handoff-sms-prompt-page': {
      'btn-submit': 'Send secure link',
      prompt:
        'To verify your identity, we need access to a camera. We’ll SMS you a secure link for taking photos. No app download is required.',
      title: 'Continue on another device',
      disclaimer:
        'For more information, see Persona‘s [Terms of Service](https://withpersona.com/legal/sms-terms-of-use) and [Privacy Policy](https://withpersona.com/legal/privacy-policy). Message and data rates may apply.',
    },
    'device-handoff-email-sent-page': {
      'btn-resend': 'Resend secure link',
      prompt:
        'We’ve sent a secure link to {{client.emailAddress}}. Return to this page after you’ve finished submitting the photos.',
      title: 'Check your device',
    },
    'device-handoff-sms-sent-page': {
      'btn-resend': 'Resend secure link',
      prompt:
        'We’ve sent a secure link to {{client.phoneNumber}}. Return to this page after you’ve finished submitting the photos.',
      title: 'Check your device',
    },
  };
}

/**
 * Creates mock combined step localizations
 *
 * Previously the string values were simply the keys themselves,
 * but they've since been updated to the actual default string values because
 * this stub is used in the CreateInquiryTemplateModal in the interactive preview
 */
export function createMockDocumentLocalizations(
  localizations: Partial<StepDocumentConfig['localizations']> = {},
): StepDocumentConfig['localizations'] {
  return {
    ...createMockPlatformLocalizations(),
    ...createMockDeviceHandoffLocalizations(),
    'capture-page': {
      'btn-take-photo': 'Take photo',
      'btn-additional-photo': 'Capture additional photo',
      'btn-finish': 'Finish',
      prompt: 'Please capture a clear photo of your supplementary document.',
    },
    'check-page': {
      title: 'Check your photo',
      prompt: 'Make sure lighting is good and any lettering is clear before continuing.',
      uploading: 'Uploading',
      'large-image-upload': 'Large image detected. This may take a while to upload.',
      'large-file-upload': 'Large file detected. This may take a while to upload.',
      'btn-submit': 'Use this photo',
      'btn-retake': 'Retake photo',
      'confirm-upload-title': 'Upload your file?',
      'confirm-upload-prompt':
        "Please ensure this is the right file. If you're uncertain, try uploading a new file.",
      'confirm-upload-uploading': 'Uploading',
      'confirm-upload-btn-submit': 'Use this file',
      'confirm-upload-btn-retake': 'Or upload a new file',
    },
    'pending-page': {
      title: 'Processing document',
      description: 'Please wait as we process your upload. This may take a few seconds.',
    },
    'prompt-page': {
      title: 'Supplementary document',
      prompt: 'Please submit clear photos of your supplementary document.',
      'btn-device-handoff': 'Continue on another device',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'btn-capture': undefined,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'btn-capture-mobile-camera': undefined,
      'btn-upload': 'Upload',
      'btn-submit': 'Submit',
      'btn-cancel': 'Cancel',
      'btn-delete': 'Yes, delete',
      'capture-options-dialog-title': 'Upload options',
      'delete-dialog-title': 'Are you sure you want to delete this file?',
      'camera-permissions-btn-continue-mobile': 'Check camera',
      'camera-permissions-btn-continue-web': 'Check camera',
      'camera-permissions-btn-cancel': 'Cancel',
      'camera-permissions-prompt':
        'Please turn on camera permissions for your browser before continuing.',
      'camera-permissions-title': "Couldn't access camera",
      'photos-permissions-body':
        'To upload from your photo library, we will need to access Photos.',
      'photos-permissions-dismiss-button-text': "Don't allow",
      'photos-permissions-enable-button-text': 'Allow',
      'photos-permissions-settings-button-text': 'Open Settings',
      'photos-permissions-settings-text':
        'Photo Library access has not been granted. Please change this in Settings.',
      'photos-permissions-title': 'Allow access to Photos',
      'no-capture-options-prompt':
        'To continue verification, please switch to a device with a camera.',
    },
    ...localizations,
  };
}
export function createMockGovIdLocalizations(
  localizations: Partial<StepGovernmentIdConfig['localizations']> = {},
): StepGovernmentIdConfig['localizations'] {
  return {
    ...createMockPlatformLocalizations(),
    ...createMockDeviceHandoffLocalizations(),
    'capture-page': {
      'auto-capture-off': 'Auto capture off',
      'auto-capture-on': 'Auto capture on',
      'btn-help': 'Help',
      'btn-take-photo': 'Take photo',
      capturing: 'Capturing...',
      'confirm-capture': 'Ensure lighting is good and lettering is clear',
      'scan-back': 'Place the back of your ID in the frame',
      'scan-front': 'Place the front of your ID in the frame',
      'scan-front-or-back': 'Place your ID in the frame',
      'scan-mrz': 'Place the barcode of your ID in the frame',
      'scan-pdf417': 'Place the barcode of your ID in the frame',
      'scan-signature': 'Place the signature of your ID in the frame',
      'barcode-capture-frame-label': 'Barcode on back of ID',
      'id-front-capture-frame-label': 'Front of ID',
      'id-back-capture-frame-label': 'Back of ID',
      'passport-portrait-capture-frame-label': 'Passport Portrait Page',
      'passport-signature-capture-frame-label': 'Passport Signature Page',
      'barcode-help-modal-title': 'Capture Tips Barcode',
      'barcode-help-modal-prompt':
        'Here are some tips to help you capture a clear photo of your barcode.',
      'barcode-help-modal-warning':
        'Your ID might have more than one barcode. The correct barcode is typically larger, with a more complicated pattern.',
      'barcode-help-modal-hints':
        '* Place your ID against a flat and dark surface\n* Align the barcode as closely as possible with the overlay.\n* Hold steady to avoid blur and glare',
      'barcode-help-modal-continue-btn': 'Continue',
      'id-front-help-modal-title': 'Capture Tips Front',
      'id-front-help-modal-prompt':
        'Here are some tips to help you capture a clear photo of your ID.',
      'id-front-help-modal-hints-desktop':
        '* Avoid backlighting from windows or bright light sources.\n* If the ID is blurry, slowly move your ID towards the camera until the image is in focus.\n* Make sure the entire ID is visible and contained in the four corners of the frame.',
      'id-front-help-modal-hints-mobile':
        '* Place your ID on a flat and dark surface. Avoid backlighting from windows or bright light sources.\n* If the ID is blurry, slowly move your ID towards the camera until the image is in focus.\n* Make sure the entire ID is visible and contained in the four corners of the frame.',
      'id-front-help-modal-continue-btn': 'Continue',
      'id-back-help-modal-title': 'Capture Tips Back',
      'id-back-help-modal-prompt':
        'Here are some tips to help you capture a clear photo of the back of your ID.',
      'id-back-help-modal-hints-desktop':
        '* Avoid backlighting from windows or bright light sources.\n* If the ID is blurry, slowly move your ID towards the camera until the image is in focus.\n* The barcode pattern may not match your ID, but make sure the ID is contained in the four corners of the frame.',
      'id-back-help-modal-hints-mobile':
        '* Place your ID on a flat and dark surface. Avoid backlighting from windows or bright light sources.\n* If the ID is blurry, slowly move your ID towards the camera until the image is in focus.\n* The barcode pattern may not match your ID, but make sure the ID is contained in the four corners of the frame.',
      'id-back-help-modal-continue-btn': 'Continue',
      'prompt-override': '',
      title: '',
    },
    'check-page': {
      title: 'Check your photo',
      'title-confirm-capture': '',
      prompt: 'Make sure lighting is good and any lettering is clear before continuing.',
      uploading: 'Uploading',
      'large-upload': 'Large images detected. This may take a while to upload.',
      'btn-submit': 'Use this photo',
      'btn-retake': 'Retake photo',
      'confirm-title': 'Upload your file?',
      'confirm-prompt':
        "Please ensure this is the right file. If you're uncertain, try uploading a new file.",
      'confirm-uploading': 'Uploading',
      'confirm-btn-submit': 'Use This File',
      'confirm-btn-retake': 'Or upload a new file',
      'barcode-capture-frame-label': 'Barcode on back of ID',
      'id-front-capture-frame-label': 'Front of ID',
      'id-back-capture-frame-label': 'Back of ID',
      'passport-portrait-capture-frame-label': 'Passport Portrait Page',
      'passport-signature-capture-frame-label': 'Passport Signature Page',
    },
    'pending-page': {
      title: 'Processing ID',
      description: 'Please wait as we process your uploads. This may take a few seconds.',
    },
    'prompt-page': {
      'title-override': '',
      'prompt-override': '',
      'btn-capture': '',
      'btn-capture-mobile-camera': '',
      'btn-device-handoff': 'Continue on another device',
      'btn-upload': 'Upload a photo',
      'camera-permissions-btn-continue-mobile': 'Check camera',
      'camera-permissions-btn-continue-web': 'Check camera',
      'camera-permissions-prompt':
        'Please turn on camera permissions for your browser before continuing.',
      'camera-permissions-title': "Couldn't access camera or microphone",
      'camera-and-mic-permissions-btn-continue-web': 'Check camera and microphone',
      'camera-and-mic-permissions-prompt':
        'Please turn on camera and microphone permissions for your browser before continuing.',
      'camera-and-mic-permissions-title': "Couldn't access camera",
      'photos-permissions-body':
        'To upload from your photo library, we will need to access Photos.',
      'photos-permissions-dismiss-button-text': "Don't allow",
      'photos-permissions-enable-button-text': 'Allow',
      'photos-permissions-settings-button-text': 'Open Settings',
      'photos-permissions-settings-text':
        'Photo Library access has not been granted. Please change this in Settings.',
      'photos-permissions-title': 'Allow access to Photos',
      'no-capture-options-prompt':
        'To continue verification, please switch to a device with a camera.',
    },
    'select-page': {
      'option-notice': 'Choose 1 of the following options',
      prompt: 'We require a photo of a government ID to verify your identity.',
      title: 'Upload a photo ID',
      'unsupported-prompt':
        'We are unable to verify identities in this country. Please select another country.',
      'unsupported-title': 'Unable to verify',
      cct: 'Citizenship Certificate',
      cid: 'Consular ID',
      dl: 'Driver License',
      foid: 'Foreigner ID',
      hic: 'Healthcare Insurance Card',
      id: 'National ID',
      ipp: 'Internal Passport',
      keyp: 'Keypass ID',
      ltpass: 'Long-Term Pass Card',
      mid: 'Military ID',
      munid: 'Municipal ID',
      myn: 'My Number Card',
      nbi: 'NBI Clearance',
      nric: 'NRIC',
      ofw: 'OFW ID',
      pan: 'PAN Card',
      pid: 'Postal ID',
      pp: 'Passport',
      ppc: 'Passport Card',
      pr: 'Permanent Resident Card',
      rp: 'Residency Permit',
      sss: 'SSS ID',
      td: 'Travel Document',
      tribalid: 'Tribal ID',
      umid: 'UMID',
      unknown: 'Other ID',
      'us-id': 'State ID',
      vid: 'Voter ID',
      visa: 'Visa',
      wp: 'Work Permit',
    },
    'auto-classification-page': {
      'unable-to-classify-document-title':
        'We couldn’t identify the ID you submitted. Please try again.',
      'unable-to-classify-document-continue-button-text': 'Continue',
      'id-class-rejected-title': 'We cannot accept this ID type. Please choose an accepted ID.',
      'id-class-rejected-continue-button-text': 'Select a different ID',
      'country-input-title': 'Country',
      'id-class-input-title': 'ID type',
      'manual-classification-title': 'Select your country and ID type',
      'manual-classification-continue-button-text': 'Continue',
      'auto-classification-capture-tip-text': 'Center your whole ID in the frame',
    },
    ...localizations,
  };
}

export function createMockSelfieLocalizations(
  localizations: Partial<StepSelfieConfig['localizations']> = {},
): StepSelfieConfig['localizations'] {
  return {
    ...createMockPlatformLocalizations(),
    ...createMockDeviceHandoffLocalizations(),
    'capture-page': {
      title: '',
      'selfie-hint-center-delayed': 'Please look into the camera and try removing your glasses',
      'selfie-hint-center-face': 'Center yourself on the screen',
      'selfie-hint-hold-still': 'Hold still…',
      'selfie-hint-look-left': 'Look slightly left',
      'selfie-hint-look-right': 'Look slightly right',
      'selfie-hint-face-too-close': 'Move further away from the camera',
      'selfie-hint-face-too-far': 'Move closer to the camera',
      'selfie-hint-multiple-faces': 'There seem to be multiple faces, find a private space',
      'selfie-hint-pose-not-center': 'Look directly at the camera',
      'selfie-hint-take-photo': 'Tap on the shutter button to take the photo',
      'selfie-hint-verifying': 'Verifying...',
    },
    'pending-page': {
      title: 'Processing selfie',
      description: 'Please wait as we process your upload. This may take a few seconds.',
    },
    'prompt-page': {
      'btn-device-handoff': 'Continue on another device',
      'btn-submit': 'Get started',
      'selfie-title': "Let's make sure you’re you",
      'selfie-prompt':
        'Position yourself in the center of the camera and then move your face left and right to show both sides.',
      'selfie-prompt-center': 'We require a photo of your face in order to verify your identity.',
      'selfie-prompt-left': 'Please take a photo while looking left.',
      'selfie-prompt-right': 'Please take a photo while looking right.',
      'stillfie-title': 'Take a Selfie',
      'stillfie-hint-left': 'Turn your head 45 degrees left',
      'stillfie-hint-right': 'Turn your head 45 degrees right',
      'stillfie-hint-wrong': "Don't turn past 45 degrees",
      'agree-to-policy':
        'By continuing, I acknowledge that Persona analyzes my facial biometrics for the purposes of identity verification and immediately deletes the biometric data after processing.',
      'no-capture-options-prompt':
        'To continue verification, please switch to a device with a camera.',
      'camera-permissions-btn-continue-mobile': 'Check camera',
      'camera-permissions-btn-continue-web': 'Check camera',
      'camera-permissions-btn-cancel': 'Cancel',
      'camera-permissions-prompt':
        'Please turn on camera permissions for your browser before continuing.',
      'camera-permissions-title': "Couldn't access camera",
      'microphone-permissions-btn-continue-mobile': 'Allow',
      'microphone-permissions-btn-continue-web': 'Check microphone',
      'microphone-permissions-btn-cancel': 'Cancel',
      'microphone-permissions-prompt': 'Please allow access to your device’s microphone.',
      'microphone-permissions-title': "Couldn't access microphone",
    },
    'check-page': {
      title: 'Review your Selfies',
      description: 'Check and make sure your selfies are what you expect',
      'selfie-label-front': 'Front',
      'selfie-label-left': 'Turned Left',
      'selfie-label-right': 'Turned Right',
      'btn-submit': 'Submit',
      'btn-retake': 'Retake Selfies',
    },
    ...localizations,
  };
}
export function createMockCompleteLocalizations(
  localizations: Partial<StepCompleteConfig['localizations']> = {},
): StepCompleteConfig['localizations'] {
  return {
    ...createMockPlatformLocalizations(),
    'child-flow-page': {
      title: 'Congratulations, you’re done!',
      prompt: 'Return to your previous device to finish.',
    },
    ...localizations,
  };
}

export function createMockGovernmentIdAssets(): StepGovernmentIdConfig['assets'] {
  return {
    'select-page': {
      'header-pictograph': null,
      'icon-drivers-license': null,
      'icon-passport': null,
      'icon-national-id': null,
      'icon-permanent-residence-id': null,
      'icon-government-id': null,
    },
    'prompt-page': {
      'header-pictograph': null,
      'id-front-pictograph': null,
      'id-back-pictograph': null,
      'passport-front-pictograph': null,
      'passport-signature-pictograph': null,
    },
    'capture-page': {
      'id-front-pictograph': null,
      'id-back-pictograph': null,
      'passport-front-pictograph': null,
      'passport-signature-pictograph': null,
      'barcode-pdf417-pictograph': null,
      'id-front-help-modal-pictograph': null,
      'id-back-help-modal-pictograph': null,
      'barcode-help-modal-pictograph': null,
    },
    'check-page': {
      'icon-file': null,
    },
    'pending-page': {
      'header-pictograph': null,
      'loading-pictograph': null,
    },
    ...createMockDeviceHandoffAssets(),
  };
}

export function createMockSelfieAssets(): StepSelfieConfig['assets'] {
  return {
    'prompt-page': {
      'header-pictograph': null,
      'selfie-pictograph': null,
      'selfie-center-pictograph': null,
      'selfie-left-pictograph': null,
      'selfie-right-pictograph': null,
    },
    'record-page': {
      'loading-pictograph': null,
      'selfie-left-pictograph': null,
      'selfie-right-pictograph': null,
    },
    ...createMockDeviceHandoffAssets(),
  };
}

export function createMockDocumentAssets(): StepDocumentConfig['assets'] {
  return {
    'prompt-page': {
      'header-pictograph': null,
      'document-pictograph': null,
    },
    'pending-page': {
      'header-pictograph': null,
      'loading-pictograph': null,
    },
    ...createMockDeviceHandoffAssets(),
  };
}

export function createMockDeviceHandoffAssets(): DeviceHandoffAssets {
  return {
    'device-handoff-prompt-page': {
      'header-pictograph': null,
      'icon-sms': null,
      'icon-email': null,
    },
    'device-handoff-started-page': {
      'header-pictograph': null,
      'device-pictograph': null,
    },
    'device-handoff-email-prompt-page': {
      'header-pictograph': null,
    },
    'device-handoff-email-sent-page': {
      'header-pictograph': null,
      'email-pictograph': null,
    },
    'device-handoff-sms-prompt-page': {
      'header-pictograph': null,
    },
    'device-handoff-sms-sent-page': {
      'header-pictograph': null,
      'sms-pictograph': null,
    },
  };
}

export function createMockVerifyWithPersonaInquiry(
  config: Partial<StepVerifyWithPersonaConfig> = {},
) {
  return createMockDynamicFlowInquiry({
    'next-step': {
      name: `${UiStepType.VerifyWithPersona}-step`,
      type: UiStepType.VerifyWithPersona,
      config: {
        'back-step-enabled': false,
        'cancel-button-enabled': false,
        pages: {
          'passkey-authentication': {
            'ui-step': {
              type: UiStepType.Ui,
              name: 'passkey-authentication',
              config: {
                'back-step-enabled': true,
                'cancel-button-enabled': true,
                components: [
                  {
                    type: UiComponentType.ButtonSubmit,
                    name: 'button-submit',
                    attributes: { text: 'Share' },
                  },
                  {
                    type: UiComponentType.ButtonSubmit,
                    name: 'button-cancel',
                    attributes: { text: 'Not now' },
                  },
                ],
              },
            },
            'component-name-mapping': {
              'confirm-button': 'button-submit',
              'cancel-button': 'button-cancel',
            },
          },
          success: {
            'ui-step': {
              type: UiStepType.Ui,
              name: 'passkey-authentication',
              config: {
                'back-step-enabled': true,
                'cancel-button-enabled': true,
                components: [
                  {
                    type: UiComponentType.ButtonSubmit,
                    name: 'button-continue',
                    attributes: {
                      text: 'Continue',
                      'auto-submit-countdown-text': 'Continue in {time}',
                      'auto-submit-interval-seconds': 3,
                    },
                  },
                ],
              },
            },
            'component-name-mapping': {
              'continue-button': 'button-continue',
            },
          },
        },
        ...config,
      } as StepVerifyWithPersonaConfig,
    },
  });
}

/**
 * Creates a dynamic flow Inquiry where the next step is a "basic" UI step.
 */
export function createMockUiInquiry(config: Partial<StepUiConfig> = {}) {
  return createMockDynamicFlowInquiry({
    'next-step': {
      type: UiStepType.Ui,
      name: 'ui',
      config: {
        'back-step-enabled': true,
        'cancel-button-enabled': true,
        components: [
          {
            type: UiComponentType.InputText,
            name: 'input-text-1',
            attributes: {
              field: 'name_first',
              placeholder: '',
              label: 'First name',
              optional: {
                logic: false,
                message: 'Required',
              },
              disabled: null,
              hidden: null,
            },
          },
          {
            type: UiComponentType.InputText,
            name: 'input-text-3',
            attributes: {
              field: 'name_last',
              disabled: null,
              hidden: null,
              placeholder: '',
              label: 'Last name',
              optional: {
                logic: false,
                message: 'Required',
              },
            },
          },
          {
            type: UiComponentType.ButtonSubmit,
            name: 'button-submit-1',
            attributes: { text: 'Submit', disabled: null, hidden: null },
          },
        ],
        localizations: createMockPlatformLocalizations(),
        terminal: false,
        ...config,
      },
    },
    'wait-for-transition': {
      'polling-mode': PollingMode.NONE,
    },
  });
}

/**
 * Creates a dynamic flow Inquiry where the next step is a preset MX Bank Integration step.
 */
export function createMockMxBankIntegrationInquiry(
  config: Partial<StepIntegrationConfigMxBank> = {},
) {
  return createMockDynamicFlowInquiry({
    'next-step': {
      name: `${UiStepType.Integration}-step`,
      type: UiStepType.Integration,
      config: {
        type: 'mx_bank',
        'to-step': 'another-cool-step',
        'widget-url': 'pizza',
        ...config,
      } as StepIntegrationConfigMxBank,
    },
  });
}

export function createMockIdClassesForCountry(): IdClassesByCountry[] {
  return [
    {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      // TODO: [PER-27557] align on lower/upper case for countrycode
      'country-code': 'us',
      'country-name': 'United States',
      'id-configs': [
        {
          class: 'pp',
          'requires-device': RequiredDevice.None,
          'requires-sides': [GovernmentIdSide.Front],
        },
        {
          class: 'dl',
          'requires-device': RequiredDevice.None,
          'requires-sides': [GovernmentIdSide.Front],
        },
      ],
    },
    {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      // TODO: [PER-27557] align on lower/upper case for countrycode
      'country-code': 'ca',
      'country-name': 'Canada',
      'id-configs': [
        {
          class: 'hic',
          'requires-device': RequiredDevice.None,
          'requires-sides': [GovernmentIdSide.Front, GovernmentIdSide.Back],
        },
        {
          class: 'pr',
          'requires-device': RequiredDevice.None,
          'requires-sides': [GovernmentIdSide.Front, GovernmentIdSide.Back],
        },
        {
          class: 'tribalid',
          'requires-device': RequiredDevice.None,
          'requires-sides': [GovernmentIdSide.Front],
        },
        {
          class: 'dl',
          'requires-device': RequiredDevice.None,
          'requires-sides': [GovernmentIdSide.Front],
        },
        {
          class: 'id',
          'requires-device': RequiredDevice.None,
          'requires-sides': [GovernmentIdSide.Front],
        },
        {
          class: 'pp',
          'requires-device': RequiredDevice.None,
          'requires-sides': [GovernmentIdSide.Front],
        },
        {
          class: 'ppc',
          'requires-device': RequiredDevice.None,
          'requires-sides': [GovernmentIdSide.Front, GovernmentIdSide.Back],
        },
      ],
    },
    {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      // TODO: [PER-27557] align on lower/upper case for countrycode
      'country-code': 'id',
      'country-name': 'Indonesia',
      'id-configs': [
        {
          class: 'id',
          'requires-device': RequiredDevice.None,
          'requires-sides': [GovernmentIdSide.Front],
        },
      ],
    },
  ];
}

export function createMockAutoclassificationConfig(
  config: Partial<AutoClassificationConfig> = {},
): AutoClassificationConfig {
  return {
    'is-enabled': true,
    'extract-text-from-image': false,
    'skip-to-country-and-id-selection': false,
    'capture-page-config': createMockAutoclassificationCaptureConfig(),
    ...config,
  };
}

export function createMockAutoclassificationCaptureConfig(): GovIdCapturePage {
  return {
    side: GovernmentIdSide.Front,
    'manual-capture-config': {
      'is-enabled': true,
      'delay-ms': 8000,
    },
    'auto-capture-config': {
      'rule-sets': [
        {
          rules: [
            {
              type: AutoCaptureRuleType.IdFront,
              'is-required': false,
              options: {},
            },
          ],
        },
      ],
    },
    overlay: {
      'overlay-fallback': ClientSideOverlay.Passport,
      overlay: {
        type: UiComponentType.ImageRemote,
        attributes: {
          url: 'google.com',
        },
        name: 'image_remote',
      },
      style: null,
    },
  };
}

export function createMockAutoclassificationResponse(
  responseType: AutoclassificationResponseType,
  requiresBothSides = false,
) {
  if (responseType === AutoclassificationResponseType.ACCEPTED) {
    return {
      'response-type': responseType,
      'id-class': requiresBothSides ? 'dl' : 'pp',
      'country-code': 'us',
      'id-config': {
        class: requiresBothSides ? 'dl' : 'pp',
        'requires-device': 'none',
        'requires-sides': requiresBothSides ? ['front', 'back'] : ['front'],
        'capture-page-configs': [
          {
            side: 'front',
            'manual-capture-config': {
              'is-enabled': true,
              'delay-ms': 8000,
            },
            'auto-capture-config': {
              'rule-sets': [
                {
                  rules: [
                    {
                      type: 'id_front',
                      'is-required': false,
                      options: {},
                    },
                  ],
                },
              ],
            },
            overlay: {
              'overlay-fallback': requiresBothSides ? 'id_front' : 'passport',
              overlay: null,
            },
          },
          requiresBothSides
            ? {
                side: 'back',
                'manual-capture-config': {
                  'is-enabled': true,
                  'delay-ms': 8000,
                },
                'auto-capture-config': {
                  'rule-sets': [
                    {
                      rules: [
                        {
                          type: 'barcode_pdf417',
                          'is-required': false,
                          options: {},
                        },
                      ],
                    },
                  ],
                },
                overlay: {
                  'overlay-fallback': 'barcode',
                  overlay: null,
                },
              }
            : null,
        ].filter(Boolean),
        icon: {
          'custom-icon': null,
          'icon-fallback': requiresBothSides ? 'card' : 'world',
        },
      },
    };
  } else {
    return {
      'response-type': responseType,
      'id-classes-by-countries': [
        {
          'country-code': 'us',
          'country-name': 'United States',
          'id-configs': [
            {
              class: 'dl',
              'requires-device': 'none',
              'requires-sides': ['front', 'back'],
              'capture-page-configs': [
                {
                  side: 'front',
                  'manual-capture-config': {
                    'is-enabled': true,
                    'delay-ms': 8000,
                  },
                  'auto-capture-config': {
                    'rule-sets': [
                      {
                        rules: [
                          {
                            type: 'id_front',
                            'is-required': false,
                            options: {},
                          },
                        ],
                      },
                    ],
                  },
                  overlay: {
                    'overlay-fallback': 'id_front',
                    overlay: null,
                  },
                },
                {
                  side: 'back',
                  'manual-capture-config': {
                    'is-enabled': true,
                    'delay-ms': 8000,
                  },
                  'auto-capture-config': {
                    'rule-sets': [
                      {
                        rules: [
                          {
                            type: 'barcode_pdf417',
                            'is-required': false,
                            options: {},
                          },
                        ],
                      },
                    ],
                  },
                  overlay: {
                    'overlay-fallback': 'barcode',
                    overlay: null,
                  },
                },
              ],
              icon: {
                'custom-icon': null,
                'icon-fallback': 'card',
              },
            },
            {
              class: 'pp',
              'requires-device': 'none',
              'requires-sides': ['front'],
              'capture-page-configs': [
                {
                  side: 'front',
                  'manual-capture-config': {
                    'is-enabled': true,
                    'delay-ms': 8000,
                  },
                  'auto-capture-config': {
                    'rule-sets': [
                      {
                        rules: [
                          {
                            type: 'id_front',
                            'is-required': false,
                            options: {},
                          },
                        ],
                      },
                    ],
                  },
                  overlay: {
                    'overlay-fallback': 'passport',
                    overlay: null,
                  },
                },
              ],
              icon: {
                'custom-icon': null,
                'icon-fallback': 'world',
              },
            },
          ],
        },
      ],
    };
  }
}

// TODO: [PER-20145] add more mock functions for Integration step
